import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { loginErrorMessage } from "@/lib/api";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    // Any stale JWT in localStorage from a previous session could make the /auth/me
    // probe on load misbehave. If we're actually rendering the login page, that means
    // AuthContext already decided the user is anonymous — nuke any leftover token to
    // guarantee the login POST goes out with no Authorization header.
    useEffect(() => {
        try { localStorage.removeItem("mss_token"); } catch (_) { /* ignore quota/private-mode */ }
    }, []);

    if (user && user !== false) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/", { replace: true });
        } catch (ex) {
            // eslint-disable-next-line no-console
            console.error("[login] failed:", ex);
            setErr(loginErrorMessage(ex));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 md:grid-cols-[1fr_460px]">
            {/* Left: brand panel */}
            <div
                className="hidden md:flex flex-col justify-between p-12"
                style={{ background: "var(--kp-black)", color: "#fff" }}
            >
                <div
                    className="inline-flex self-start items-center"
                >
                    <img
                        src="/kp-logo-dark.png"
                        alt="KP Industrial"
                        className="h-11 w-auto"
                        style={{ maxWidth: "260px" }}
                    />
                </div>
                <div>
                    <div className="font-display font-bold text-white text-5xl leading-[1.05] tracking-tight">
                        MSS PNG
                        <br />
                        Payroll<span style={{ color: "var(--kp-red)" }}>.</span>
                    </div>
                    <p className="mt-4 max-w-sm text-neutral-400 text-sm leading-relaxed">
                        Fortnightly payroll for MSS (PNG) Limited. Defensible against IRC
                        Annexure B, to the cent.
                    </p>
                </div>
                <div className="text-[11px] text-neutral-600 uppercase tracking-[0.2em] font-display">
                    MSS (PNG) Limited · Payroll
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-8 bg-white">
                <form onSubmit={onSubmit} className="w-full max-w-sm">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display mb-2">
                        Sign in
                    </div>
                    <h1 className="font-display font-bold text-3xl text-[color:var(--kp-charcoal)]">
                        Welcome back.
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1 mb-8">
                        MSS (PNG) Limited payroll — sign in to continue.
                    </p>

                    <label className="block text-[11px] uppercase tracking-[0.16em] text-neutral-600 font-display mb-1">
                        Email
                    </label>
                    <input
                        data-testid={AUTH.loginEmailInput}
                        autoFocus
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="kp-focus w-full border border-neutral-300 px-3 py-2.5 text-sm mb-4 outline-none rounded-[3px]"
                        placeholder="you@example.com"
                        required
                    />
                    <label className="block text-[11px] uppercase tracking-[0.16em] text-neutral-600 font-display mb-1">
                        Password
                    </label>
                    <input
                        data-testid={AUTH.loginPasswordInput}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="kp-focus w-full border border-neutral-300 px-3 py-2.5 text-sm mb-6 outline-none rounded-[3px]"
                        placeholder="••••••••"
                        required
                    />

                    {err && (
                        <div
                            data-testid={AUTH.loginError}
                            className="text-sm mb-4 px-3 py-2 border-l-2"
                            style={{
                                borderLeftColor: "var(--kp-red)",
                                background: "#FFF5F5",
                                color: "#8A0713",
                            }}
                        >
                            {err}
                        </div>
                    )}

                    <button
                        data-testid={AUTH.loginSubmitBtn}
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 text-sm font-display font-semibold uppercase tracking-[0.14em] text-white transition-colors rounded-[3px] disabled:opacity-70"
                        style={{ background: "var(--kp-red)" }}
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                    <div className="mt-8 text-[11px] text-neutral-400 font-mono-money">
                        MSS (PNG) Limited · KP Industrial
                    </div>
                </form>
            </div>
        </div>
    );
}
